import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import { tokenLastUseSentence, tokenMintSentence } from '@/lib/token-labels';
import type { OperatorToken } from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';

/**
 * « Révoquer ce jeton ? », on the pattern of the three boxes of §4 (§20.2,
 * issue #84).
 *
 * `VersionedDialog` is REUSED and not copied, the way `session-delete-dialog`
 * reuses it: it is what lays the veil, wires Escape, and moves the focus to
 * **Annuler** on mount - the property that matters here, because a stray Enter
 * must not put out the key of a machine somebody is working on.
 *
 * ## The tone IS `destructive`, unlike the sessions box
 *
 * That one refuses red because a jettisoned session comes back from the trash
 * for thirty days. Here nothing comes back: `revokeOperatorToken` writes a date
 * and there is no route that removes it. Red is kept for what cannot be undone,
 * which is exactly this - and the box says so in words rather than leaving it
 * to be guessed from a colour.
 *
 * ## It NAMES the token, and says what goes with it
 *
 * The label in the description, with its mint day and its last use: tokens look
 * alike in a list, the identifiers are UUIDs, and the only thing that tells two
 * rows apart is what their owner wrote on them. The last use is there because
 * it is the question asked in the second before the click - « does this one
 * still serve? ».
 */

export interface TokenRevokeDialogProps {
  readonly token: OperatorToken;
  readonly busy: boolean;
  /** The refusal, worded by the caller. Null when there is none. */
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function TokenRevokeDialog({
  token,
  busy,
  error,
  onConfirm,
  onCancel,
}: TokenRevokeDialogProps) {
  return (
    <VersionedDialog
      data-testid="jetons-confirmation"
      title="Révoquer ce jeton ?"
      description={`« ${token.label} ». ${tokenMintSentence(token)}. ${tokenLastUseSentence(token)}.`}
      confirmLabel="Révoquer le jeton"
      confirmTone="destructive"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <VersionedDialogSection
        title="Ce qui se passe"
        hint="La ligne reste dans la liste, grisée et datée : c’est la seule trace de ce que ce jeton a ouvert."
      >
        <p className="text-gray-700 text-small">
          Il cesse aussitôt d’ouvrir l’assistant MCP. Un poste qui le porte encore dans{' '}
          <code className="font-mono">MATTER_PAT</code> devra en recevoir un neuf.
        </p>
      </VersionedDialogSection>

      <Alert tone="danger" title="C’est irréversible">
        Un jeton révoqué ne se réactive pas. Il n’y a rien à restaurer : le serveur n’a jamais gardé
        que son empreinte, donc remplacer ce jeton veut dire en frapper un autre.
      </Alert>
    </VersionedDialog>
  );
}
